import { BaseMetaColumn, BaseMetaTable, FilterItem, FilterItemOperator } from "namirasoft-core";
import { JobRow, JobStatusRow } from "namirasoft-job-arranger";
import { DateTimeFormatter, NSButtonBlue, NSButtonRed, NSDialogBoxDateTime, NSDialogDelete, NSLoading, NSRouterMakerResult, NSRow } from "namirasoft-site-react";
import { Component, CSSProperties } from "react";
import { JobArrangerProps } from "../JobArrangerProps";
import { JobStatusObject } from "../JobStatusObject";
import Styles from "./JobStatusPanel.module.css";

export interface JobStatusHistoryProps extends NSRouterMakerResult, JobArrangerProps
{
    job: JobRow;
    // Called after a history row is updated or deleted.
    onChange?: () => void;
}

export interface JobStatusHistoryState
{
    rows: JobStatusRow[] | null;
    loading: boolean;
    edit_row: JobStatusRow | null;
    delete_row: JobStatusRow | null;
    deleting: boolean;
}

export class JobStatusHistory extends Component<JobStatusHistoryProps, JobStatusHistoryState>
{
    private readonly dateFormatter = new DateTimeFormatter();
    // NS buttons default to 272x48; shrink them to fit a table row.
    private readonly actionButtonStyle: CSSProperties = { width: "auto", height: "1.75rem", padding: "0 0.75rem", fontSize: "0.8125rem", fontWeight: 500 };

    constructor(props: JobStatusHistoryProps)
    {
        super(props);
        this.state = { rows: null, loading: false, edit_row: null, delete_row: null, deleting: false };
    }

    override componentDidMount(): void
    {
        this.load();
    }

    override componentDidUpdate(prevProps: Readonly<JobStatusHistoryProps>): void
    {
        if (this.props.job.id !== prevProps.job.id)
            this.setState({ rows: null }, () => this.load());
    }

    async load()
    {
        this.setState({ loading: true });
        try
        {
            // Filter the history to this job. job_status has no workspace, so all_workspaces is null.
            const table = new BaseMetaTable(null, "job_status", "job_status");
            const column = new BaseMetaColumn(table, "job_id", "job_id", "String", true);
            const filters = [new FilterItem(table, column, false, FilterItemOperator.all.equals, this.props.job.id)];

            const res = await this.props.servers.job_arranger.job_status.List(filters, null, null, [], null);
            this.setState({ rows: res.rows, loading: false });
        }
        catch (error)
        {
            this.setState({ rows: [], loading: false });
        }
    }

    async updateDate(row: JobStatusRow, created_at: Date)
    {
        await this.props.servers.job_arranger.job_status.Update(row.id, { created_at });
        this.setState({ edit_row: null });
        this.props.notifier.onSuccess(`The ${row.status} date is changed to ${this.dateFormatter.format(created_at)}`);
        this.load();
        this.props.onChange?.();
    }

    async delete(row: JobStatusRow)
    {
        this.setState({ deleting: true });
        try
        {
            await this.props.servers.job_arranger.job_status.Delete(row.id);
            this.setState({ delete_row: null, deleting: false });
            this.props.notifier.onSuccess(`The ${row.status} status is removed from the history`);
            this.load();
            this.props.onChange?.();
        }
        catch (error)
        {
            this.setState({ deleting: false });
            this.props.notifier.onError(error as Error);
        }
    }

    renderEditDialog()
    {
        const row = this.state.edit_row;
        if (!row)
            return null;
        return (
            <NSDialogBoxDateTime
                onClose={() => this.setState({ edit_row: null })}
                box={{
                    required: true,
                    title: `${row.status} Date`,
                    defaultValue: new Date(row.created_at)
                }}
                button={{
                    title: "Update",
                    onClick: {
                        showLoading: true,
                        action: (onFinished, box) =>
                        {
                            try
                            {
                                const created_at = box?.getValue();
                                if (!created_at)
                                {
                                    onFinished();
                                    return;
                                }
                                this.updateDate(row, created_at)
                                    .catch(error => this.props.notifier.onError(error as Error))
                                    .finally(() => onFinished());
                            }
                            catch (error)
                            {
                                this.props.notifier.onError(error as Error);
                                onFinished();
                            }
                        }
                    }
                }}
            >
                <></>
            </NSDialogBoxDateTime>
        );
    }

    renderDeleteDialog()
    {
        const row = this.state.delete_row;
        if (!row)
            return null;
        return (
            <NSDialogDelete
                title="Delete Status"
                description={`Are you sure you want to delete the ${row.status} status of ${this.dateFormatter.format(row.created_at)}?`}
                show_as_code={false}
                loading={this.state.deleting}
                onClose={() => this.setState({ delete_row: null })}
                onYes={{ showLoading: false, action: () => this.delete(row) }}
                onNo={{ showLoading: false, action: () => this.setState({ delete_row: null }) }}
            />
        );
    }

    override render()
    {
        if (this.state.loading)
            return <div className={Styles.ja_panel}><NSLoading /></div>;

        const rows = this.state.rows ?? [];

        return (
            <div className={Styles.ja_panel}>
                <h4 className={Styles.ja_panel_title}>Status History:</h4>

                {
                    rows.length === 0
                        ?
                        <span>No status changes recorded yet.</span>
                        :
                        <div className={Styles.ja_panel_table_wrapper}>
                            <table className={Styles.ja_panel_table}>
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        rows.map(row => (
                                            <tr
                                                key={row.id}
                                                style={{ background: JobStatusObject.get(row.status)?.color }}
                                            >
                                                <td style={{ fontWeight: 500 }}>{row.status}</td>
                                                <td>{this.dateFormatter.format(row.created_at)}</td>
                                                <td>
                                                    <NSRow style={{ justifyContent: "flex-start", gap: "0.5rem" }}>
                                                        <NSButtonBlue
                                                            title="Update"
                                                            style={this.actionButtonStyle}
                                                            onClick={{ showLoading: false, action: () => this.setState({ edit_row: row }) }}
                                                        />
                                                        <NSButtonRed
                                                            title="Delete"
                                                            style={this.actionButtonStyle}
                                                            onClick={{ showLoading: false, action: () => this.setState({ delete_row: row }) }}
                                                        />
                                                    </NSRow>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                }

                {this.renderEditDialog()}
                {this.renderDeleteDialog()}
            </div>
        );
    }
}